import { track } from '@betterment/js-runtime';
import { Box, Button, DropdownMenu } from '@betterment/samba-react';

import { TransactionButtonsTransactionOption } from './transaction-buttons/TransactionButtons';

import { locationAssign } from '../path-helpers';
import { urlBuilder } from '../urlBuilder';

const menuOptions = {
  TRANSFER: { eventName: 'GoalTransfer', text: 'Transfer within Betterment' },
  RolloverOption: { eventName: 'Rollover', text: 'Rollover to Betterment' },
  CharitableDonationOption: { eventName: 'Charity', text: 'Donate to charity' },
  WITHDRAWAL: { eventName: 'Withdraw', text: 'Withdraw' },
  view_transfers: { eventName: 'TransferCenter', text: 'View transfers' },
} as const;

type Props = {
  transactionOptions: TransactionButtonsTransactionOption[];
  subAccountId?: number;
  isCompact?: boolean;
};

type ValueOf<T> = T[keyof T];
type EventNames = (typeof menuOptions)[keyof typeof menuOptions]['eventName'];

// TODO: Delete this component and refactor home screen to use TransactionButtons
const TransferOrRolloverDropdown = ({ transactionOptions, isCompact = false }: Props) => {
  const onMenuItemClick = (eventName: EventNames) => {
    let url = '';
    track('ElementClicked', { module: 'DropdownMenu', name: eventName });

    switch (eventName) {
      case 'GoalTransfer':
        url = urlBuilder.showTransferSetup.absolute();
        break;
      case 'Rollover':
        url = urlBuilder.getRollovers.absolute();
        break;
      case 'Charity':
        url = urlBuilder.appCharitableGiftStart.absolute();
        break;
      case 'Withdraw':
        url = urlBuilder.appWithdrawalSetup.absolute();
        break;
      case 'TransferCenter':
        url = urlBuilder.getPendingTransfers.absolute();
        break;
    }

    locationAssign(url);
  };

  const menuItem = (menuOption: ValueOf<typeof menuOptions>) => (
    <DropdownMenu.Item key={menuOption.eventName} onClick={() => onMenuItemClick(menuOption.eventName)}>
      {menuOption.text}
    </DropdownMenu.Item>
  );

  const menuDropdownOptions = transactionOptions.flatMap(option => {
    const key = option.__typename === 'TransferOption' ? option.legacyTransferType : option.__typename;
    const menuOption = menuOptions[key as keyof typeof menuOptions];
    return menuOption ? menuItem(menuOption) : [];
  });

  const hasDropdownOptions = menuDropdownOptions.length > 0;
  if (hasDropdownOptions) {
    const viewTransfersMenuOption = menuOptions.view_transfers;
    const option = (
      <Box key={`separator-${viewTransfersMenuOption.eventName}`}>
        <DropdownMenu.Separator css={{ my: 'space.100' }} />
        {menuItem(viewTransfersMenuOption)}
      </Box>
    );
    menuDropdownOptions.push(option);
  }

  const hasRolloverOption = transactionOptions.some(option => option.__typename === 'RolloverOption');
  const buttonText = !isCompact && hasRolloverOption ? 'Transfer or rollover' : 'Transfer';

  return (
    <DropdownMenu>
      {hasDropdownOptions && (
        <DropdownMenu.Trigger asChild={true}>
          <Button
            variant="solidSecondary"
            css={{
              textWrap: 'nowrap',
              width: {
                base: '100%',
                md: 'auto',
              },
            }}
            spacing={isCompact ? 'compact' : 'default'}
          >
            {buttonText}
            <Button.Icon name="CaretDown" />
          </Button>
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content align="end">{menuDropdownOptions}</DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default TransferOrRolloverDropdown;
